import { supabase } from '../lib/supabase';
import {
  AdminUpdateApplicationStatusRequest,
  AdminUpdateApplicationStatusResponse,
} from '../types';

/**
 * 관리자: 무통장입금 확인 처리
 * payment_status를 pending → completed로 변경 + SMS 발송
 */
export const adminConfirmPayment = async (
  applicationId: string,
  paymentNote?: string
): Promise<{ success: boolean; error?: { code: string; message: string } }> => {
  try {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    // Call Edge Function (입금 확인 + SMS 발송)
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

    const response = await fetch(`${supabaseUrl}/functions/v1/admin-confirm-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        application_id: applicationId,
        payment_note: paymentNote || null,
      }),
    });

    const responseData = await response.json();
    console.log('Admin confirm payment response:', { status: response.status, data: responseData });

    if (!responseData.success) {
      return {
        success: false,
        error: {
          code: responseData.error?.code || 'UNKNOWN_ERROR',
          message: responseData.error?.message || '입금확인 처리 중 오류가 발생했습니다.',
        },
      };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Admin confirm payment error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

/**
 * 관리자: 신청 상태 변경 (waiting→registered, →cancelled 등)
 */
export const adminUpdateApplicationStatus = async (
  request: AdminUpdateApplicationStatusRequest
): Promise<AdminUpdateApplicationStatusResponse> => {
  try {
    console.log('Admin update application status request:', request);

    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    // Call Edge Function
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

    const response = await fetch(`${supabaseUrl}/functions/v1/admin-update-application-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify(request),
    });

    const responseData = await response.json();
    console.log('Admin update application status response:', { status: response.status, data: responseData });

    return responseData;
  } catch (error: any) {
    console.error('Admin update application status error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: '예상치 못한 오류가 발생했습니다.',
        details: error.message,
      },
    };
  }
};

/**
 * 관리자: 결제기한 연장
 */
export const adminExtendDeadline = async (
  applicationId: string,
  newDeadline: string
): Promise<{ success: boolean; error?: { code: string; message: string }; data?: any }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

    const response = await fetch(`${supabaseUrl}/functions/v1/admin-extend-deadline`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        application_id: applicationId,
        new_deadline: newDeadline,
      }),
    });

    const responseData = await response.json();
    console.log('Admin extend deadline response:', { status: response.status, data: responseData });

    return responseData;
  } catch (error: any) {
    console.error('Admin extend deadline error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};

/**
 * 관리자: 취소된 신청 복구
 */
export const adminRestoreApplication = async (
  applicationId: string,
  forceRestore?: boolean
): Promise<{ success: boolean; error?: { code: string; message: string }; requireConfirmation?: boolean; currentStudents?: number; capacity?: number; data?: any }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: '로그인이 필요합니다.',
        },
      };
    }

    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

    const response = await fetch(`${supabaseUrl}/functions/v1/admin-restore-application`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        application_id: applicationId,
        force_restore: forceRestore || false,
      }),
    });

    const responseData = await response.json();
    console.log('Admin restore application response:', { status: response.status, data: responseData });

    return responseData;
  } catch (error: any) {
    console.error('Admin restore application error:', error);
    return {
      success: false,
      error: {
        code: 'UNEXPECTED_ERROR',
        message: error.message,
      },
    };
  }
};
